import { faEye, faPen, faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { OIAnyOtherIncomeDetailsForm } from ".";
import { IFormITR1, IOtherIncomes, IReviewOSIncomes } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const OIAnyOtherIncomeDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<IOtherIncomes | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
  const OtherIncomesDetails = values.IncomeDetails.OtherIncomes;

  const hasNonEmptyValues = (incomeData: IOtherIncomes) => {
    const {
      AnyOnlineGaming,
      AnyInvoiceDiscounting,
      AnyOtherIncomes,
      DepreciationSection57Incomes,
      UnderSection89AIncomes,
      IncomeNonNotifiedCountryAmount,
      IncomeTaxablePreviousYear89A,
      TaxationRelief89AAmount
    } = incomeData;

    const valuesToCheck = [
      ...(AnyOnlineGaming[0] || []),
      ...(AnyInvoiceDiscounting[0] || []),
      ...(AnyOtherIncomes[0] || []),
      ...(DepreciationSection57Incomes[0] || []),
      ...(UnderSection89AIncomes[0] || []),
      IncomeNonNotifiedCountryAmount,
      IncomeTaxablePreviousYear89A,
      TaxationRelief89AAmount
    ];

    return valuesToCheck.some((value) => value !== "");
  };

  const handleTotalCalculation = (incomeData: IOtherIncomes) => {
    const AnyOnlineGaming =
      incomeData?.AnyOnlineGaming?.reduce((acc, curr) => {
        const value = Number(curr[1]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const AnyInvoiceDiscounting =
      incomeData?.AnyInvoiceDiscounting?.reduce((acc, curr) => {
        const value = Number(curr[1]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const AnyOtherIncomes =
      incomeData?.AnyOtherIncomes?.reduce((acc, curr) => {
        const value = Number(curr[1]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const Section57Incomes =
      incomeData?.DepreciationSection57Incomes?.reduce((acc, curr) => {
        const value = Number(curr[1]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const Section89AIncomes =
      incomeData?.UnderSection89AIncomes?.reduce((acc, curr) => {
        const value = Number(curr[1]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const TaxationRelief89A = Number(incomeData?.TaxationRelief89AAmount || "0");
    const IncomeNonNotified = Number(incomeData?.IncomeNonNotifiedCountryAmount || "0");
    const IncomePreviousYear = Number(incomeData?.IncomeTaxablePreviousYear89A || "0");

    const TotalOtherIncome =
      AnyOnlineGaming +
      AnyInvoiceDiscounting +
      AnyOtherIncomes +
      Section57Incomes +
      Section89AIncomes +
      TaxationRelief89A +
      IncomeNonNotified +
      IncomePreviousYear;

    const OtherSourcesIncomes: IReviewOSIncomes = {
      ...values.OthersInc,
      TotalOtherIncome: TotalOtherIncome
    };
    const TotalIncomeOthSrc = Object.values(OtherSourcesIncomes)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      OthersInc: OtherSourcesIncomes,
      IncomeOthSrc: TotalIncomeOthSrc
    };

    setValues({ ...cloneDeep(values), ...DATA });
    if (hasNonEmptyValues(incomeData)) {
      setFieldValue("IncomeDetails.OtherIncomes", incomeData);
    } else {
      setFieldValue("IncomeDetails.OtherIncomes", undefined);
    }
  };

  return (
    <>
      <section>
        <div className="section-title-wrap">
          <h5>
            Other Income
            <small>If you have any other income like dividends, received gifts etc.</small>
          </h5>
          <PreventEditComponent>
            {OtherIncomesDetails ? (
              <Button
                variant="warning"
                size="sm"
                className="text-white"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {OtherIncomesDetails ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <RenderListItem
              title="Online Gaming, Lottery, Gifts and more"
              incomes={OtherIncomesDetails?.AnyOnlineGaming}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Online Gaming, Lottery, Gifts and more");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  AnyOnlineGaming: [["", "", ""]]
                });
              }}
            />

            <RenderListItem
              title="Invoice Discounting"
              incomes={OtherIncomesDetails?.AnyInvoiceDiscounting}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Invoice Discounting");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  AnyInvoiceDiscounting: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Any Other Sources Income"
              incomes={OtherIncomesDetails?.AnyOtherIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Any Other Sources Income");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  AnyOtherIncomes: [["", "", ""]]
                });
              }}
            />

            <RenderListItem
              title="Expense u/s 57"
              incomes={OtherIncomesDetails?.DepreciationSection57Incomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Expense u/s 57");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  DepreciationSection57Incomes: [["", ""]]
                });
              }}
            />

            <RenderListItem
              title="Income u/s 89A (notified country)"
              incomes={OtherIncomesDetails?.UnderSection89AIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Income u/s 89A (notified country)");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  UnderSection89AIncomes: [["", "", ""]]
                });
              }}
            />

            <RenderSingleListItem
              title="Income claimed for taxation relief u/s 89A"
              sum={OtherIncomesDetails?.TaxationRelief89AAmount || ""}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Income claimed for taxation relief u/s 89A");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  TaxationRelief89AAmount: ""
                });
              }}
            />

            <RenderSingleListItem
              title="Income u/s 89A (non-notified country)"
              sum={OtherIncomesDetails?.IncomeNonNotifiedCountryAmount || ""}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Income u/s 89A (non-notified country)");
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  IncomeNonNotifiedCountryAmount: ""
                });
              }}
            />

            <RenderSingleListItem
              title="Income taxable during the previous year on which relief u/s 89A was claimed"
              sum={OtherIncomesDetails?.IncomeTaxablePreviousYear89A || ""}
              onDeleteBtnClick={() => {
                setDeleteMsg(
                  () =>
                    "Income taxable during the previous year on which relief u/s 89A was claimed"
                );
                setShowDeleteModal({
                  ...OtherIncomesDetails,
                  IncomeTaxablePreviousYear89A: ""
                });
              }}
            />
          </dl>
        ) : null}
      </section>

      <OIAnyOtherIncomeDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(incomeData) => {
          if (incomeData) {
            handleTotalCalculation(incomeData);
          }
          setShowAddFormCanvas(() => false);
        }}
        formValues={OtherIncomesDetails}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>{`You want to delete ${deleteMsg}.`}</p>}
        onYesBtnClick={() => {
          if (showDeleteModal) {
            handleTotalCalculation(showDeleteModal);
            setShowDeleteModal(() => null);
          }
        }}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default OIAnyOtherIncomeDetails;

type RenderItemProps = {
  incomes: string[][];
  title: string;
  onDeleteBtnClick: () => void;
};

const RenderListItem: React.FC<RenderItemProps> = (props: RenderItemProps) => {
  const { incomes, title, onDeleteBtnClick } = props;
  let sum = 0;
  let hasValue = false;

  if (incomes?.length) {
    incomes.forEach((item) => {
      if (item[0] || item[1] || item[2]) {
        hasValue = true;
      }

      if (item.length && item[1]) {
        sum += parseInt(item[1]);
      }
    });
  }

  return (
    <>
      {hasValue ? (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
            <PreventEditComponent>
              <Button
                variant="transparent"
                className="icon-only"
                onClick={() => onDeleteBtnClick()}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
            </PreventEditComponent>
          </dd>
        </div>
      ) : null}
    </>
  );
};

const RenderSingleListItem = ({
  title,
  sum = "0",
  onDeleteBtnClick
}: {
  title: string;
  sum: string;
  onDeleteBtnClick: () => void;
}) => {
  const shouldDisplay = sum !== "" && Number(sum) >= 0;

  return (
    <>
      {shouldDisplay && (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
            <PreventEditComponent>
              <Button
                variant="transparent"
                className="icon-only"
                onClick={() => onDeleteBtnClick()}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
            </PreventEditComponent>
          </dd>
        </div>
      )}
    </>
  );
};
